import React, { Component } from "react";
import { getLPContractAmount, getLPContractEndDate, getLPContractNo, getLPLabel, getLPMunNo, getLPSize, LPProps } from "./LeaseTable";
import GridDataView, { GRID_SIZE } from "../DataView/GridDataView";
import { ImperialSystem } from "@my-dld/utils/UnitUtils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { EjariProperty, EjariPropertyType } from "@eres/ejari-api/Entity";
import { resetTenantContext, updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import { downloadTenancyContract } from "@my-dld/actions/PropertyActions";
import Strings from "@my-dld/assets/strings";
import { LocaleUtil } from "@eres/core";
import { getEjariContractStatusClass } from "@eres/ejari-api/EjariPropertyUtil";
import { normalizedPropertyId } from "@eres/ejari-api/Utils";

interface State {
	searchKey?: string;
	page: number;
}

class LeaseCard extends Component<LPProps, State> {
	constructor( props: LPProps ) {
		super( props );
		this.state = {
			page: 1,
		};
	}

	render() {
		const { properties, type, unit } = this.props;
		if ( properties == null || properties.length < 1 ) {
			return null;
		}
		const { searchKey } = this.state;
		const filterData = this.getFilterData( searchKey );
		let Row = LandItem;
		switch ( type ) {
			case EjariPropertyType.VIRTUAL_VILLA:
			case EjariPropertyType.VILLA:
			case EjariPropertyType.BUILDING:
				Row = VillaItem;
				break;
			case EjariPropertyType.UNIT:
				Row = UnitItem;
				break;
		}
		return <GridDataView
			title={ `${ getLPLabel( type ) } (${ properties!.length })` }
			items={ this.getPageData( filterData ) }
			onSearch={ this.onSearch }
			renderGridItem={ ( item: EjariProperty, index: number ) => {
				return <Row
					key={ normalizedPropertyId( item ) + "-" + index }
					property={ item }
					isSqFt={ unit.Id === ImperialSystem.Id }
					onDownload={ downloadTenancyContract }
					onDetails={ this.openDetails }/>;
			} }
			totalCount={ !isEmptyStringOrNull( searchKey ) ? filterData.length : properties.length }
			onPageChange={ this.onPageChange }/>;
	}

	private getFilterData( sKey?: string ) {
		const { properties, type, unit } = this.props;
		if ( properties && sKey ) {
			return properties.filter( ( item: EjariProperty ) => {
				const filterKeys: string[] = [];
				filterKeys.push( LocaleUtil.getLocalValue( item.ContractStatusName ) );
				filterKeys.push( getLPMunNo( item ) );
				filterKeys.push( LocaleUtil.getLocalValue( item.Area ) );
				filterKeys.push( getLPSize( item, unit.Id === ImperialSystem.Id ) );
				filterKeys.push( getLPContractNo( item ) );
				filterKeys.push( getLPContractAmount( item ) );
				filterKeys.push( getLPContractEndDate( item ) );
				if ( type === EjariPropertyType.VIRTUAL_VILLA || type === EjariPropertyType.VILLA || type === EjariPropertyType.BUILDING ) {
					filterKeys.push( LocaleUtil.getLocalValue( item.BuildingName ) );
				}
				if ( type === EjariPropertyType.UNIT ) {
					filterKeys.push( item.UnitNo );
					filterKeys.push( LocaleUtil.getLocalValue( item.BuildingName ) );
				}
				const fItem = filterKeys.find( ( fKey: string ) => fKey.toLocaleLowerCase().includes( sKey.toLocaleLowerCase() ) );
				return fItem != undefined;
			} );
		}
		return properties ? properties : [];
	}

	private getPageData( filterData: EjariProperty[] ) {
		const { page } = this.state;
		return filterData.slice( ( page - 1 ) * GRID_SIZE, page * GRID_SIZE );
	}

	private onSearch = ( sKey?: string ) => {
		this.setState( {
			searchKey: sKey,
			page: 1
		} );
	};

	private onPageChange = ( page: number ) => {
		this.setState( { page: page } );
	};

	private openDetails = ( property: EjariProperty ) => {
		resetTenantContext();
		updateTenantContext( { property: property } );
		navigateWithInParent( "mydld", "tenant/lease/details" );
	};
}

function LandItem( props: {
	property: EjariProperty, isSqFt: boolean, onDownload: ( p: EjariProperty ) => void,
	onDetails: ( p: EjariProperty ) => void,
} ) {
	const { property, isSqFt, onDownload, onDetails } = props;
	return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
        <div className="card">
            <div className="custom-card-title">
                <div>
                    <span>{ getLPMunNo( property ) }</span>
                    <span>{ Strings.land_num_label }</span>
                </div>
                <span className={ `property-status ${ getEjariContractStatusClass( property.ContractStatus ) }` }>
                    { LocaleUtil.getLocalValue( property.ContractStatusName ) }
                </span>
            </div>
			<div className="card-body custom-body">
				<div className="detail-holder">
					<div className="row no-gutters property-card-detail">
						<div className="col-12">
							<span>{ LocaleUtil.getLocalValue( property.Area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-6">
							<span>{ getLPContractNo( property ) }</span>
							<span>{ Strings.contract_num_sign }</span>
						</div>
						<div className="col-6">
							<span>{ getLPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-6">
							<span className="contract-amount">{ getLPContractAmount( property ) }</span>
							<span>{ Strings.contract_amount_aed }</span>
						</div>
						<div className="col-6">
							<span>{ getLPContractEndDate( property ) }</span>
							<span>{ Strings.contract_end_date_label }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon
                property={property} onDownload={onDownload}
                onDetails={onDetails} />
		</div>
	</div>;
}

function VillaItem( props: {
	property: EjariProperty, isSqFt: boolean, onDownload: ( p: EjariProperty ) => void,
	onDetails: ( p: EjariProperty ) => void,
} ) {
	const { property, isSqFt, onDownload, onDetails } = props;
	return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
        <div className="card">
            <div className="custom-card-title">
                <div>
                    <span>{LocaleUtil.getLocalValue(property.BuildingName)}</span>
                    <span>{Strings.name}</span>
                </div>
                <span className={`property-status ${getEjariContractStatusClass(property.ContractStatus)}`}>
                    {LocaleUtil.getLocalValue(property.ContractStatusName)}
                </span>
            </div>
			<div className="card-body custom-body">
				<div className="detail-holder">
					<div className="row no-gutters property-card-detail">
						<div className="col-12">
							<span>{ LocaleUtil.getLocalValue( property.Area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-4">
							<span>{ getLPContractNo( property ) }</span>
							<span>{ Strings.contract_num_sign }</span>
						</div>
						<div className="col-4">
							<span>{ getLPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
						<div className="col-4">
							<span>{ getLPContractEndDate( property ) }</span>
							<span>{ Strings.contract_end_date_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-6">
							<span>{ getLPMunNo( property ) }</span>
							<span>{ Strings.land_num_label }</span>
						</div>
						<div className="col-6">
							<span className="contract-amount">{ getLPContractAmount( property ) }</span>
							<span>{ Strings.contract_amount_aed }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon
                property={property} onDownload={onDownload}
                onDetails={onDetails} />
		</div>
	</div>;
}

function UnitItem( props: {
	property: EjariProperty, isSqFt: boolean, onDownload: ( p: EjariProperty ) => void,
	onDetails: ( p: EjariProperty ) => void,
} ) {
	const { property, isSqFt, onDownload, onDetails } = props;
	return <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 col-12 property-card">
        <div className="card">
            <div className="custom-card-title">
                <div>
                    <span>{LocaleUtil.getLocalValue(property.BuildingName)}</span>
                    <span>{Strings.building_name}</span>
                </div>
                <span className={`property-status ${getEjariContractStatusClass(property.ContractStatus)}`}>
                    {LocaleUtil.getLocalValue(property.ContractStatusName)}
                </span>
            </div>
			<div className="card-body custom-body">
				<div className="detail-holder">
					<div className="row no-gutters property-card-detail">
						<div className="col-12">
							<span>{ LocaleUtil.getLocalValue( property.Area ) }</span>
							<span>{ Strings.area_label }</span>
						</div>
					</div>
					<div className="row no-gutters property-card-detail">
						<div className="col-4">
							<span>{ getLPContractNo( property ) }</span>
							<span>{ Strings.contract_num_sign }</span>
						</div>
						<div className="col-4">
							<span>{ getLPSize( property, isSqFt ) }</span>
							<span>{ Strings.size }</span>
						</div>
						<div className="col-4">
							<span>{ getLPContractEndDate( property ) }</span>
							<span>{ Strings.contract_end_date_label }</span>
						</div>

					</div>
					<div className="row no-gutters property-card-detail col-item-3">
						<div className="col-4">
							<span>{ property.UnitNo }</span>
							<span>{ Strings.Unit_Number_Label }</span>
						</div>
						<div className="col-4">
							<span>{ getLPMunNo( property ) }</span>
							<span>{ Strings.land_num_label }</span>
						</div>
						<div className="col-4">
							<span className="contract-amount">{ getLPContractAmount( property ) }</span>
							<span>{ Strings.contract_amount_aed }</span>
						</div>
					</div>
				</div>
            </div>
            <RenderCommon
                property={property} onDownload={onDownload}
                onDetails={onDetails} />
		</div>
	</div>;
}

function RenderCommon( props: {
	property: EjariProperty, onDownload: ( p: EjariProperty ) => void,
	onDetails: ( p: EjariProperty ) => void,
} ) {
	const { property, onDownload, onDetails } = props;
	return <div className="property-card-action">
        <button
            className="btn btn_1_outline"
            onClick={() => onDownload(property)}>{Strings.download_contract}</button>
        <button
            className="btn btn_1_outline"
            onClick={() => onDetails(property)}>{Strings.view_details}</button>
	</div>;
}

export default LeaseCard;
